import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '../ui';
import { S3FileUpload } from '../ui';
import { DynamicTable, Column } from './DynamicTable';
import { maskCurrency, unmaskCurrency, maskNumber } from '../../utils/masks';
import type { PatrimonyData, LandProperty } from '../../types';
import type { UploadedFileInfo } from '../../services/api';

// Cria um novo imóvel com valores padrão
const createNewLandProperty = (): LandProperty => ({
  id: `land-${Date.now()}`,
  propertyName: '',
  location: '',
  owner: '',
  plantingArea: 0,
  cattleArea: 0,
  reserveArea: 0,
  valueUsd: 0,
});

export interface PatrimonySectionProps {
  data: Partial<PatrimonyData>;
  onLandPropertiesChange: (properties: LandProperty[]) => void;
  onFileUpload: (fileInfo: UploadedFileInfo) => void;
  disabled?: boolean;
  token?: string;
}

export const PatrimonySection: React.FC<PatrimonySectionProps> = ({
  data,
  onLandPropertiesChange,
  onFileUpload,
  disabled = false,
  token = '',
}) => {
  const { t } = useTranslation();

  // Colunas da tabela de imóveis — recalcular apenas quando disabled muda
  const landPropertyColumns: Column<LandProperty>[] = useMemo(
    () => [
      {
        key: 'propertyName',
        header: t('patrimony.denomination'),
        render: (item, _index, onChange) => (
          <Input
            value={item.propertyName}
            onChange={(value) => onChange({ ...item, propertyName: value })}
            placeholder="Nome do imóvel"
            disabled={disabled}
            containerClassName="mb-0"
          />
        ),
      },
      {
        key: 'location',
        header: t('patrimony.location'),
        render: (item, _index, onChange) => (
          <Input
            value={item.location}
            onChange={(value) => onChange({ ...item, location: value })}
            placeholder="Cidade/UF"
            disabled={disabled}
            containerClassName="mb-0"
          />
        ),
      },
      {
        key: 'owner',
        header: t('patrimony.owner'),
        render: (item, _index, onChange) => (
          <Input
            value={item.owner}
            onChange={(value) => onChange({ ...item, owner: value })}
            placeholder="Nome"
            disabled={disabled}
            containerClassName="mb-0"
          />
        ),
      },
      {
        key: 'plantingArea',
        header: t('patrimony.plantingArea'),
        render: (item, _index, onChange) => (
          <Input
            value={item.plantingArea?.toString() || ''}
            onChange={(value) => onChange({ ...item, plantingArea: parseFloat(value) || 0 })}
            type="number"
            disabled={disabled}
            containerClassName="mb-0"
          />
        ),
      },
      {
        key: 'cattleArea',
        header: t('patrimony.cattleArea'),
        render: (item, _index, onChange) => (
          <Input
            value={item.cattleArea?.toString() || ''}
            onChange={(value) => onChange({ ...item, cattleArea: parseFloat(value) || 0 })}
            type="number"
            disabled={disabled}
            containerClassName="mb-0"
          />
        ),
      },
      {
        key: 'valueUsd',
        header: t('patrimony.valueUsd'),
        render: (item, _index, onChange) => (
          <Input
            value={item.valueUsd ? maskCurrency(item.valueUsd, 'USD') : ''}
            onChange={(value) => onChange({ ...item, valueUsd: unmaskCurrency(value) })}
            placeholder="$ 0.00"
            disabled={disabled}
            containerClassName="mb-0"
          />
        ),
      },
    ],
    [disabled, t]
  );

  // Total calculado das áreas e valor dos imóveis cadastrados
  const landProperties = data.landProperties ?? [];
  const totalArea = landProperties.reduce(
    (acc, p) => acc + (p.plantingArea || 0) + (p.cattleArea || 0) + (p.reserveArea || 0),
    0
  );
  const totalValue = landProperties.reduce((acc, p) => acc + (p.valueUsd || 0), 0);

  return (
    <div className="space-y-6">
      {/* a. Patrimônio em Terras */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-4">{t('patrimony.landProperties')}</h4>
        <DynamicTable
          columns={landPropertyColumns}
          data={landProperties}
          onChange={onLandPropertiesChange}
          createNewItem={createNewLandProperty}
          addButtonText={t('patrimony.addProperty')}
          disabled={disabled}
        />

        {landProperties.length > 0 && (
          <div className="mt-4 p-3 bg-green-50 rounded-lg">
            <p className="text-sm text-green-800">
              <strong>{t('patrimony.totalPatrimony')}:</strong>{' '}
              {maskNumber(totalArea, 2)} Ha |{' '}
              {maskCurrency(totalValue, 'USD')}
            </p>
          </div>
        )}
      </div>

      {/* Upload de documentos de patrimônio */}
      {token && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            {t('patrimony.docsTitle')}
          </h4>
          <p className="text-xs text-gray-500 mb-4">
            {t('patrimony.docsDescription')}
          </p>
          <S3FileUpload
            token={token}
            documentType="patrimony"
            label={t('patrimony.uploadLabel')}
            description={t('patrimony.uploadDescription')}
            disabled={disabled}
            onUploadComplete={onFileUpload}
          />
        </div>
      )}
    </div>
  );
};

export default PatrimonySection;
